import { CommissioningDraft } from './types';

export function renameProposedFloor(
  draft: CommissioningDraft,
  floorKey: string,
  name: string,
): CommissioningDraft {
  const floor = requireFloor(draft, floorKey);
  if (floor.existingFloorId)
    throw new Error('Existing Floors cannot be renamed');
  return {
    ...draft,
    floors: draft.floors.map((item) =>
      item.key === floorKey ? { ...item, name } : item,
    ),
  };
}

export function assignUnitFloor(
  draft: CommissioningDraft,
  unitKey: string,
  floorKey: string | undefined,
): CommissioningDraft {
  const unit = requireUnit(draft, unitKey);
  if (unit.existingUnitId) throw new Error('Existing Units cannot be moved');
  if (floorKey) requireFloor(draft, floorKey);
  return {
    ...draft,
    units: draft.units.map((item) =>
      item.key === unitKey ? { ...item, floorKey } : item,
    ),
  };
}

export function removeFloor(draft: CommissioningDraft, floorKey: string) {
  const floor = requireFloor(draft, floorKey);
  if (floor.existingFloorId)
    throw new Error('Existing Floors cannot be removed');
  const orphanedUnitKeys = draft.units
    .filter((unit) => !unit.existingUnitId && unit.floorKey === floorKey)
    .map(({ key }) => key);
  return {
    draft: {
      ...draft,
      floors: draft.floors.filter(({ key }) => key !== floorKey),
      units: draft.units.map((unit) =>
        !unit.existingUnitId && unit.floorKey === floorKey
          ? { ...unit, floorKey: undefined }
          : unit,
      ),
    },
    orphanedUnitKeys,
  };
}

export function removeUnit(draft: CommissioningDraft, unitKey: string) {
  const unit = requireUnit(draft, unitKey);
  if (unit.existingUnitId) throw new Error('Existing Units cannot be removed');
  const affectedDeviceKeys = draft.devices
    .filter(({ unitKey: destination }) => destination === unitKey)
    .map(({ key }) => key);
  return {
    draft: {
      ...draft,
      units: draft.units.filter(({ key }) => key !== unitKey),
      devices: draft.devices.map((device) =>
        device.unitKey === unitKey ? { ...device, unitKey: undefined } : device,
      ),
      needsReview: [
        ...new Set([
          ...draft.needsReview,
          ...draft.devices
            .filter((device) => !device.ignored && device.unitKey === unitKey)
            .map((device) => device.discoveredDeviceId),
        ]),
      ],
    },
    affectedDeviceKeys,
  };
}

export function assignDeviceDestination(
  draft: CommissioningDraft,
  deviceKey: string,
  unitKey: string | undefined,
): CommissioningDraft {
  if (!draft.devices.some(({ key }) => key === deviceKey)) {
    throw new Error('Commissioning device was not found');
  }
  if (unitKey) requireUnit(draft, unitKey);
  return {
    ...draft,
    devices: draft.devices.map((device) =>
      device.key === deviceKey ? { ...device, unitKey } : device,
    ),
  };
}

export function summarizeDraft(draft: CommissioningDraft) {
  const errors = draft.validationErrors.map(({ message }) => message);
  const duplicateUnitNames = duplicateNewNames(
    draft.units.map((unit) => ({
      name: unit.name,
      existingId: unit.existingUnitId,
    })),
  );
  if (duplicateUnitNames.length > 0) {
    errors.push('Unit names must be unique within the Building.');
  }
  if (
    duplicateNewNames(
      draft.floors.map((floor) => ({
        name: floor.name,
        existingId: floor.existingFloorId,
      })),
    ).length > 0
  ) {
    errors.push('Floor names must be unique within the Building.');
  }
  const unitKeys = new Set(draft.units.map((unit) => unit.key));
  if (
    draft.devices.some(
      (device) =>
        !device.ignored && (!device.unitKey || !unitKeys.has(device.unitKey)),
    )
  ) {
    errors.push('Every accepted device must have a destination Unit.');
  }
  if (draft.needsReview.length > 0) {
    errors.push('All inferred items must be reviewed before provisioning.');
  }
  const usedUnitKeys = new Set(
    draft.devices
      .filter((device) => !device.ignored)
      .map((device) => device.unitKey),
  );
  const plannedUnits = draft.units.filter(
    (unit) => unit.existingUnitId || usedUnitKeys.has(unit.key),
  );
  const usedFloorKeys = new Set(
    plannedUnits
      .filter((unit) => !unit.existingUnitId)
      .map((unit) => unit.floorKey),
  );
  const plannedFloors = draft.floors.filter(
    (floor) => floor.existingFloorId || usedFloorKeys.has(floor.key),
  );
  return {
    counts: {
      floors: plannedFloors.length,
      units: plannedUnits.length,
      devices: draft.devices.length,
      ignored: draft.devices.filter(({ ignored }) => ignored).length,
    },
    errors,
    newFloors: plannedFloors.filter((floor) => !floor.existingFloorId).length,
    newUnits: plannedUnits.filter((unit) => !unit.existingUnitId).length,
    existingFloors: plannedFloors.filter((floor) => !!floor.existingFloorId)
      .length,
    existingUnits: plannedUnits.filter((unit) => !!unit.existingUnitId).length,
    canProvision: errors.length === 0 && draft.status === 'draft',
  };
}

function duplicateNewNames(
  values: Array<{ name: string; existingId?: string | null }>,
) {
  const counts = new Map<string, number>();
  for (const { name } of values) {
    const normalized = name.trim().toLowerCase();
    counts.set(normalized, (counts.get(normalized) ?? 0) + 1);
  }
  return values.filter(
    ({ name, existingId }) =>
      !existingId && (counts.get(name.trim().toLowerCase()) ?? 0) > 1,
  );
}

function requireFloor(draft: CommissioningDraft, key: string) {
  const floor = draft.floors.find((item) => item.key === key);
  if (!floor) throw new Error('Commissioning Floor was not found');
  return floor;
}

function requireUnit(draft: CommissioningDraft, key: string) {
  const unit = draft.units.find((item) => item.key === key);
  if (!unit) throw new Error('Commissioning Unit was not found');
  return unit;
}
